import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

import api from "@/lib/api";
import { PageHeader, btn, Loader, Empty, Pagination } from "@/components/Page";
import { MiningTabs } from "@/components/mining/MiningTabs";
import { MiningEquipmentLink } from "@/components/mining/MiningEquipmentLink";

const PER_PAGE = 30;

export default function MiningRankings() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const res = await api.get(`/mining/rankings?page=${targetPage}&limit=${PER_PAGE}`);
      setData(res.data);
    } catch {
      setData({ leaderboard: [], page: targetPage, total_pages: 1, has_prev: false, has_next: false, total: 0, per_page: PER_PAGE });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
  }, [page]);

  if (loading && !data) return <Loader label="Loading Mining Rankings..." />;

  const rows = data?.leaderboard || [];

  return (
    <div data-testid="mining-rankings-page" className="space-y-6">
      <PageHeader
        title="Mining Rankings"
        subtitle="See where your lanista stands in the mines."
        action={<button type="button" data-testid="mining-rankings-refresh-button" onClick={() => load(page)} className={btn("ghost")}>Refresh</button>}
      />

      <MiningEquipmentLink />

      <MiningTabs />

      <section data-testid="mining-rankings-me-panel" className="grid md:grid-cols-3 gap-3">
        <div className="border border-amber-900/30 bg-zinc-950/80 rounded-sm p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Your current rank</div>
          <div className="font-cinzel text-2xl text-amber-300 mt-2">{data?.me ? `#${data.me.rank}` : "Not ranked"}</div>
        </div>
        <div className="border border-amber-900/30 bg-zinc-950/80 rounded-sm p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Your Mining level</div>
          <div className="font-cinzel text-2xl text-zinc-100 mt-2">{data?.me ? data.me.mining_level.toLocaleString() : "—"}</div>
        </div>
        <div className="border border-amber-900/30 bg-zinc-950/80 rounded-sm p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Your total Mining XP</div>
          <div className="font-cinzel text-2xl text-zinc-100 mt-2">{data?.me ? data.me.total_mining_xp.toLocaleString() : "—"}</div>
        </div>
      </section>

      <section data-testid="mining-rankings-panel" className="border border-amber-900/30 bg-zinc-950/80 rounded-sm overflow-x-auto">
        <div className="px-4 py-4 border-b border-amber-900/30 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-wide">Top Mining Lanistae</h2>
          </div>
          <div data-testid="mining-rankings-count" className="text-xs uppercase tracking-[0.18em] text-zinc-500">{data?.total || 0} ranked lanistae</div>
        </div>

        {rows.length === 0 ? <div className="p-4"><Empty label="No Mining rankings yet." /></div> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">Lanista</th>
                <th className="text-right px-4 py-3">Mining Level</th>
                <th className="text-right px-4 py-3">Total Mining XP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.user_id} data-testid={`mining-rankings-row-${row.user_id}`} className="border-b border-zinc-900/80 last:border-b-0">
                  <td className="px-4 py-3 font-cinzel text-amber-300">#{row.rank}</td>
                  <td className="px-4 py-3">
                    <Link data-testid={`mining-ranking-lanista-${row.user_id}`} to={`/profile/${row.user_id}`} className="text-amber-300 hover:text-amber-200 underline underline-offset-2">
                      {row.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-100" data-testid={`mining-ranking-level-${row.user_id}`}>{row.mining_level.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-zinc-300" data-testid={`mining-ranking-xp-${row.user_id}`}>{row.total_mining_xp.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div className="border border-amber-900/30 bg-zinc-950/70 rounded-sm px-4 py-3">
        <Pagination page={data?.page || 1} totalPages={data?.total_pages || 1} onChange={setPage} testid="mining-rankings-pagination" prevLabel="Previous" nextLabel="Next" />
      </div>
    </div>
  );
}